import React from 'react'
import { createRoot, type Root } from 'react-dom/client'

import { TextAnalyticsChart } from './chart/TextAnalyticsChart'
import { LinkedReportContainer } from './linkedReport/LinkedReportContainer'
import { WorkspaceFilters } from './workspaceFilters/WorkspaceFilters'

const roots = new WeakMap<HTMLElement, Root>()

const parseJson = <T>(raw: string | null | undefined, fallback: T): T => {
	if (raw === null || raw === undefined || raw === '') {
		return fallback
	}
	try {
		return JSON.parse(raw) as T
	} catch {
		return fallback
	}
}

const ensureRoot = (el: HTMLElement): Root => {
	let root = roots.get(el)
	if (root === undefined) {
		root = createRoot(el)
		roots.set(el, root)
	}
	return root
}

export const bootstrapTextAnalytics = (scope: ParentNode = document): void => {
	scope.querySelectorAll<HTMLElement>('[data-react-component="text-analytics-chart"]').forEach((el) => {
		const blockId = Number(el.dataset.blockId)
		const endpoints = parseJson(el.dataset.endpoints, null)
		if (endpoints === null) {
			return
		}
		// Chart options are read from window.__taChartOptions[blockId], which is populated
		// by the inline <script> tag rendered next to this mount point by PHP.
		// selfSubscribe: true — this mount is always the standalone/parent chart (a linked child
		// chart is rendered by LinkedReportContainer directly, not through this mount point), so
		// it must subscribe to the bus itself to re-render on workspace-filter changes.
		ensureRoot(el).render(
			React.createElement(TextAnalyticsChart, {
				blockId,
				endpoints,
				selfSubscribe: true,
			}),
		)
	})

	scope.querySelectorAll<HTMLElement>('[data-react-component="text-analytics-linked-report"]').forEach((el) => {
		const blockId = Number(el.dataset.blockId)
		const initialState = parseJson(el.dataset.initialState, null)
		const endpoints = parseJson(el.dataset.endpoints, null)
		if (initialState === null || endpoints === null) {
			return
		}
		const placeholderHtml = el.dataset.placeholderHtml ?? ''
		const csvFilename = el.dataset.csvFilename ?? 'verbatims'
		const chartType = el.dataset.chartType ?? 'column'
		// Initial chart options (when displayType=chart on first render) are emitted by PHP
		// as a sibling <script> tag that populates window.__taChartOptions[blockId].
		ensureRoot(el).render(
			React.createElement(LinkedReportContainer, {
				blockId,
				initialState,
				placeholderHtml,
				endpoints,
				csvFilename,
				chartType,
			}),
		)
	})

	scope.querySelectorAll<HTMLElement>('[data-react-component="text-analytics-workspace-filters"]').forEach((el) => {
		const blockId = Number(el.dataset.blockId)
		const applyUrl = el.dataset.applyUrl
		const resetUrl = el.dataset.resetUrl
		if (applyUrl === undefined || resetUrl === undefined) {
			return
		}
		const resetLabel =
			el.dataset.resetLabel === undefined || el.dataset.resetLabel === '' ? 'Reset' : el.dataset.resetLabel
		const monthPlaceholder =
			el.dataset.monthPlaceholder === undefined || el.dataset.monthPlaceholder === ''
				? 'Select month'
				: el.dataset.monthPlaceholder
		ensureRoot(el).render(
			React.createElement(WorkspaceFilters, {
				blockId,
				applyUrl,
				resetUrl,
				months: parseJson(el.dataset.months, []),
				sentiments: parseJson(el.dataset.sentiments, []),
				initialSelectedMonths: parseJson(el.dataset.selectedMonths, []),
				initialSelectedSentiment:
					(el.dataset.selectedSentiment ?? '') === ''
						? null
						: (el.dataset.selectedSentiment as 'positive' | 'neutral' | 'negative'),
				resetLabel,
				monthPlaceholder,
			}),
		)
	})
}
